import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import axios from "axios";
import config from "../config/config";
import { useAuth } from "../pages/login/AuthContext";

const CAROUSEL_INTERVAL_MS = 5000;
const STORAGE_KEY_PREFIX = "sistemMesajPopUp_seen";

/** Bugünün tarihini YYYY-MM-DD (yerel) olarak döndürür */
function getTodayDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Bu kullanıcı bugün pop-up'ı gördü mü? */
function wasPopUpSeenToday(userId) {
  if (!userId || typeof localStorage === "undefined") return false;
  const key = `${STORAGE_KEY_PREFIX}_${userId}_${getTodayDateStr()}`;
  return localStorage.getItem(key) === "1";
}

/** Pop-up kapatıldı, bugün için işaretle */
function markPopUpSeenToday(userId) {
  if (!userId || typeof localStorage === "undefined") return;
  const key = `${STORAGE_KEY_PREFIX}_${userId}_${getTodayDateStr()}`;
  localStorage.setItem(key, "1");
}

function getResimFullUrl(path) {
  if (!path) return "";
  const p = String(path).trim();
  if (p.startsWith("http")) return p;
  const m = p.match(/\/api\/uploads\/pop-up\/([^/]+)$/);
  if (m) return `${config.baseURL}/bildirim/pop-up-resim/${encodeURIComponent(m[1])}`;
  const filenameOnly = p.includes("/") ? p.replace(/^.*\//, "") : p;
  if (filenameOnly && /\.(jpe?g|png|gif|webp)$/i.test(filenameOnly)) {
    return `${config.baseURL}/bildirim/pop-up-resim/${encodeURIComponent(filenameOnly)}`;
  }
  const base = config.baseURL.replace(/\/api\/?$/, "");
  return `${base}${p.startsWith("/") ? "" : "/"}${p}`;
}

function downloadResim(url, defaultFilename = "resim.jpg") {
  if (!url) return;
  const filename = (url.split("/").pop() || defaultFilename).split("?")[0] || defaultFilename;
  fetch(url, { credentials: "include" })
    .then((r) => r.blob())
    .then((blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(() => window.open(url, "_blank"));
}

/**
 * Giriş yapan kullanıcıya pop-up olarak atanmış sistem mesajlarını gösterir.
 * Resimler 5 saniyede bir otomatik kayar; ileri/geri ile manuel geçiş yapılabilir.
 * "Kapat" ile kapatıldığında sadece bu oturumda gizlenir.
 */
export function SistemMesajPopUp() {
  const { token, userData } = useAuth();
  const [list, setList] = useState([]);
  const [dismissedCount, setDismissedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [resimIndex, setResimIndex] = useState(0);
  const [imageSize, setImageSize] = useState(null);
  const [currentImageLoaded, setCurrentImageLoaded] = useState(false);

  const userId = userData?.Id ?? userData?.id ?? userData?.KullaniciAdi ?? userData?.username ?? null;

  const current = list[dismissedCount];
  const resimlerRaw = current?.PopUpResimler;
  const resimler = (() => {
    if (!resimlerRaw) return [];
    if (Array.isArray(resimlerRaw)) return resimlerRaw;
    if (typeof resimlerRaw === "string") {
      try {
        const parsed = JSON.parse(resimlerRaw);
        return Array.isArray(parsed) ? parsed : [];
      } catch { return []; }
    }
    return [];
  })();
  const hasResim = resimler.length > 0;

  const maxW = typeof window !== "undefined" ? window.innerWidth * 0.9 : 600;
  const maxH = typeof window !== "undefined" ? window.innerHeight * 0.85 : 500;

  const handleImageLoad = useCallback((e) => {
    const nw = e.target.naturalWidth;
    const nh = e.target.naturalHeight;
    if (!nw || !nh) return;
    let w = nw;
    let h = nh;
    if (w > maxW || h > maxH) {
      const r = Math.min(maxW / w, maxH / h);
      w = Math.round(w * r);
      h = Math.round(h * r);
    }
    setImageSize({ w, h });
    setCurrentImageLoaded(true);
  }, [maxW, maxH]);

  // Geçişte container boyutunu koru; sadece mesaj değişince sıfırla (beyaz flaşı önler)
  useEffect(() => {
    if (current?.Id != null) setImageSize(null);
  }, [current?.Id]);

  // Resim değişince yükleme bayrağını sıfırla; onLoad ile tekrar true yapılacak
  useEffect(() => {
    setCurrentImageLoaded(false);
  }, [resimIndex, current?.Id]);

  // Sonraki ve önceki resmi önceden yükle ki geçişte beyaz görünmesin
  useEffect(() => {
    if (!hasResim || resimler.length < 2) return;
    const nextIdx = (resimIndex + 1) % resimler.length;
    const prevIdx = (resimIndex - 1 + resimler.length) % resimler.length;
    const preload = (idx) => {
      const url = getResimFullUrl(resimler[idx]);
      if (url) {
        const img = new Image();
        img.src = url;
      }
    };
    preload(nextIdx);
    preload(prevIdx);
  }, [hasResim, resimler, resimIndex]);

  useEffect(() => {
    if (!token) {
      setList([]);
      setDismissedCount(0);
      setResimIndex(0);
      return;
    }
    setLoading(true);
    axios
      .get(`${config.baseURL}/bildirim/sistem-mesaj/popup`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          if (wasPopUpSeenToday(userId)) {
            setList([]);
          } else {
            setList(res.data.data);
            setResimIndex(0);
          }
        } else {
          setList([]);
        }
      })
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [token, userId]);

  // userId sonradan yüklendiyse ve bugün zaten gösterildiyse listeyi gizle
  useEffect(() => {
    if (list.length > 0 && userId && wasPopUpSeenToday(userId)) {
      setList([]);
    }
  }, [userId, list.length]);

  useEffect(() => {
    if (dismissedCount < list.length) setResimIndex(0);
  }, [dismissedCount, list.length]);

  useEffect(() => {
    if (!hasResim || resimler.length <= 1) return;
    const t = setInterval(() => {
      setResimIndex((i) => (i + 1) % resimler.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [hasResim, resimler.length]);

  const open = !loading && list.length > 0 && dismissedCount < list.length;
  const hasNext = dismissedCount + 1 < list.length;

  const handleClose = useCallback(() => {
    if (hasNext) {
      setDismissedCount((c) => c + 1);
    } else {
      markPopUpSeenToday(userId);
      setDismissedCount(list.length);
    }
  }, [hasNext, list.length, userId]);

  const goPrev = () => {
    setResimIndex((i) => (i - 1 + resimler.length) % resimler.length);
  };
  const goNext = () => {
    setResimIndex((i) => (i + 1) % resimler.length);
  };

  if (!current) return null;

  const contentWidth = hasResim && imageSize ? imageSize.w : undefined;
  const contentMaxWidth = hasResim ? maxW : "sm";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 4,
          border: "1px solid",
          borderColor: "var(--primary-color, #1976d2)",
          maxWidth: contentMaxWidth,
          width: contentWidth || "100%",
          minWidth: 280,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "var(--primary-color)",
          fontWeight: 600,
          pr: 1,
        }}
      >
        <Typography component="span" variant="h6">
          {current.Baslik}
        </Typography>
        <IconButton size="small" onClick={handleClose} aria-label="Kapat">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          overflow: "hidden",
          pt: hasResim ? 0 : 2,
          px: hasResim ? 0 : 3,
          pb: hasResim ? 0 : 2,
          "&:first-of-type": { paddingTop: hasResim ? 0 : 2 },
        }}
      >
        {hasResim && (
          <Box sx={{ mb: current.Icerik ? 2 : 0 }}>
            <Box
              sx={{
                position: "relative",
                width: imageSize ? imageSize.w : "100%",
                height: imageSize ? imageSize.h : "auto",
                maxWidth: "100%",
                maxHeight: maxH,
                borderRadius: 0,
                overflow: "hidden",
                bgcolor: "#f5f5f5",
                display: "block",
                lineHeight: 0,
                fontSize: 0,
              }}
            >
              <img
                src={getResimFullUrl(resimler[resimIndex])}
                alt={`Pop-up resim ${resimIndex + 1}`}
                onLoad={handleImageLoad}
                style={{
                  width: imageSize ? imageSize.w : "100%",
                  height: imageSize ? imageSize.h : "auto",
                  maxWidth: "100%",
                  maxHeight: maxH,
                  objectFit: "contain",
                  display: "block",
                  verticalAlign: "top",
                  opacity: currentImageLoaded ? 1 : 0,
                  transition: "opacity 0.2s ease-out",
                }}
              />
              <IconButton
                onClick={() => downloadResim(getResimFullUrl(resimler[resimIndex]), `popup-resim-${resimIndex + 1}.jpg`)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "rgba(255,255,255,0.9)",
                  "&:hover": { bgcolor: "white" },
                }}
                size="small"
                aria-label="İndir"
              >
                <DownloadIcon />
              </IconButton>
              {resimler.length > 1 && (
                <>
                  <IconButton
                    onClick={goPrev}
                    sx={{
                      position: "absolute",
                      left: 4,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(255,255,255,0.9)",
                      "&:hover": { bgcolor: "white" },
                    }}
                    size="small"
                    aria-label="Önceki"
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                  <IconButton
                    onClick={goNext}
                    sx={{
                      position: "absolute",
                      right: 4,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(255,255,255,0.9)",
                      "&:hover": { bgcolor: "white" },
                    }}
                    size="small"
                    aria-label="Sonraki"
                  >
                    <NavigateNextIcon />
                  </IconButton>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      left: "50%",
                      transform: "translateX(-50%)",
                      display: "flex",
                      gap: 0.5,
                    }}
                  >
                    {resimler.map((_, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: i === resimIndex ? "var(--primary-color)" : "rgba(255,255,255,0.7)",
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}
        {(current.Icerik || !hasResim) && (
          <Typography
            variant="body1"
            component="div"
            sx={{ whiteSpace: "pre-wrap", px: hasResim ? 2 : 0, pt: hasResim && current.Icerik ? 1 : 0 }}
          >
            {current.Icerik || "—"}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            bgcolor: "var(--primary-color)",
            "&:hover": { bgcolor: "var(--secondary-color)" },
          }}
        >
          {hasNext ? "Sonrakine geç" : "Kapat"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
